import type { IncidentLocationType, ParentTicketFormValues, TicketPartFormRow } from '@/types/ticket'
import type { ParentTicketWithMeta } from '@/store/parentTicket'
import type { FEDispatchSelection } from '@/components/forms/parentTicket/FEDispatchFlow'
import { buildTicketRequestSubject } from '@/lib/utils/ticketRequestSubject'
import { formAttachmentRowsFromTicket, mapApiAttachments } from '@/lib/utils/parentTicketAttachments'

function toNumberOrUndefined(value: unknown): number | undefined {
  if (value == null || value === '') return undefined
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}

function toNumberOrNull(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function toBooleanOrNull(value: unknown): boolean | null | undefined {
  if (value === undefined) return undefined
  if (value === null || value === '') return null
  if (typeof value === 'boolean') return value
  if (value === 'true' || value === '1') return true
  if (value === 'false' || value === '0') return false
  return null
}

/** Build update payload from cached ticket (for task panels PATCH via PUT). */
export function ticketToFormValues(
  ticket: ParentTicketWithMeta & Record<string, unknown>,
  ticketId: string,
): ParentTicketFormValues | null {
  if (!ticket?.requestId && !ticket.id) return null

  const ext = ticket as Record<string, unknown>
  let assets = ext.assets
  if (typeof assets === 'string') {
    try {
      assets = JSON.parse(assets) as unknown[]
    } catch {
      assets = []
    }
  }
  if (!Array.isArray(assets)) assets = []

  const subject =
    buildTicketRequestSubject({
      subject: ext.subject as string | undefined,
      request_number: ticket.requestId,
      clientName: ticket.clientName,
      clientOrg: ticket.clientOrg,
    }) || ''
  const incidentLocation = ext.incidentLocation as Record<string, unknown> | null | undefined
  const incidentLocationType = (
    incidentLocation?.type ??
    ext.incidentLocationType ??
    ext.incident_location_type ??
    ''
  ) as ParentTicketFormValues['incidentLocationType']

  return {
    requestId: ticket.requestId || '',
    storeId:
      (ticket as { store_id?: number }).store_id ??
      (ticket as ParentTicketWithMeta).store?.storeId ??
      (typeof ext.storeId === 'number' || typeof ext.storeId === 'string' ? ext.storeId : undefined),
    storeCode:
      (ticket as { store_code?: string }).store_code ??
      (ticket as ParentTicketWithMeta).store?.storeCode ??
      (ext.storeCode as string) ??
      '',
    incidentLocationType,
    incidentLocationName: String(
      incidentLocation?.name ??
      ext.incidentLocationName ??
      ext.incident_location_name ??
      '',
    ),
    incidentLocationAddress: String(
      incidentLocation?.address ??
      ext.incidentLocationAddress ??
      ext.incident_location_address ??
      '',
    ),
    incidentLocationLatitude: toNumberOrNull(
      incidentLocation?.latitude ??
      ext.incidentLocationLatitude ??
      ext.incident_location_latitude,
    ),
    incidentLocationLongitude: toNumberOrNull(
      incidentLocation?.longitude ??
      ext.incidentLocationLongitude ??
      ext.incident_location_longitude,
    ),
    incidentLocationPlaceId:
      (incidentLocation?.placeId as string | number | null | undefined) ??
      (ext.incidentLocationPlaceId as string | number | null | undefined) ??
      (ext.incident_location_place_id as string | number | null | undefined) ??
      null,
    incidentLocationSource:
      (incidentLocation?.source as string | null | undefined) ??
      (ext.incidentLocationSource as string | null | undefined) ??
      (ext.incident_location_source as string | null | undefined) ??
      null,
    incidentLocationMunicipality:
      (incidentLocation?.municipality as string | null | undefined) ??
      (ext.incidentLocationMunicipality as string | null | undefined) ??
      (ext.incident_location_municipality as string | null | undefined) ??
      null,
    incidentLocationProvince:
      (incidentLocation?.province as string | null | undefined) ??
      (ext.incidentLocationProvince as string | null | undefined) ??
      (ext.incident_location_province as string | null | undefined) ??
      null,
    incidentLocationCountry:
      (incidentLocation?.country as string | null | undefined) ??
      (ext.incidentLocationCountry as string | null | undefined) ??
      (ext.incident_location_country as string | null | undefined) ??
      null,
    ticketId: (ext.ticket_number ?? ext.ticketId ?? ticketId) as string,
    subject,
    priority: (ticket.severity === 'Mid' ? 'Medium' : ticket.severity) || '',
    severity: String(ticket.severity ?? ext.priority ?? '3').replace(/^Severity\s+/i, '') || '3',
    supportType:
      (ext.supportType as string) ??
      (ticket as { support_type?: string }).support_type ??
      '',
    serviceTypeId:
      toNumberOrUndefined(ext.serviceTypeId) ??
      toNumberOrUndefined(ext.service_type_id),
    source: (ext.source as string) ?? '',
    issueDescription: ticket.description || '',
    agentId: ext.agentId != null ? String(ext.agentId) : '',
    assignedTeamId:
      toNumberOrUndefined(ext.assignedTeamId) ??
      toNumberOrUndefined(ext.assigned_team_id),
    resolverTeamId:
      toNumberOrUndefined(ext.resolverTeamId) ??
      toNumberOrUndefined(ext.resolver_team_id),
    clientReferenceNumber:
      (ext.clientReferenceNumber as string) ??
      (ext.client_reference_number as string) ??
      '',
    billable: toBooleanOrNull(ext.billable) ?? null,
    poTrigger: Boolean(ext.poTrigger ?? ext.po_trigger ?? false),
    hasPartsNeeded: Boolean(ext.hasPartsNeeded ?? ext.has_parts_needed ?? false),
    parts: mapPartsFromTicket(ext),
    issueCategoryId:
      toNumberOrUndefined(ext.issueCategoryId) ??
      toNumberOrUndefined(ext.issue_category_id),
    issueSubCategoryId:
      toNumberOrUndefined(ext.issueSubCategoryId) ??
      toNumberOrUndefined(ext.issue_sub_category_id),
    issueItemId:
      toNumberOrUndefined(ext.issueItemId) ??
      toNumberOrUndefined(ext.issue_item_id),
    regionId:
      toNumberOrUndefined(ext.regionId) ??
      toNumberOrUndefined(ext.region_id),
    areaClusterId:
      toNumberOrUndefined(ext.areaClusterId) ??
      toNumberOrUndefined(ext.area_cluster_id),
    provinceId:
      toNumberOrUndefined(ext.provinceId) ??
      toNumberOrUndefined(ext.province_id),
    townId:
      toNumberOrUndefined(ext.townId) ??
      toNumberOrUndefined(ext.town_id),
    clientPurchaseOrderId:
      ext.clientPurchaseOrderId != null
        ? Number(ext.clientPurchaseOrderId)
        : ext.client_purchase_order_id != null
          ? Number(ext.client_purchase_order_id)
          : undefined,
    certificationRequired: Boolean(
      ext.certificationRequired ?? ext.certification_required ?? false,
    ),
    brandId: (() => {
      const required = Boolean(ext.certificationRequired ?? ext.certification_required ?? false)
      if (!required) return null
      return (
        toNumberOrNull(ext.brandId) ??
        toNumberOrNull(ext.brand_id)
      )
    })(),
    notes: ext.notes != null ? String(ext.notes) : '',
    assets: assets as ParentTicketFormValues['assets'],
    hasAttachmentsNeeded: mapApiAttachments(ext.attachments).some((item) => item.source === 'form'),
    attachmentRows: formAttachmentRowsFromTicket(mapApiAttachments(ext.attachments)),
  }
}

function clearedIncidentLocationFields(): Pick<
  ParentTicketFormValues,
  | 'incidentLocationType'
  | 'incidentLocationName'
  | 'incidentLocationAddress'
  | 'incidentLocationLatitude'
  | 'incidentLocationLongitude'
  | 'incidentLocationPlaceId'
  | 'incidentLocationSource'
  | 'incidentLocationMunicipality'
  | 'incidentLocationProvince'
  | 'incidentLocationCountry'
> {
  return {
    incidentLocationType: '',
    incidentLocationName: '',
    incidentLocationAddress: '',
    incidentLocationLatitude: null,
    incidentLocationLongitude: null,
    incidentLocationPlaceId: null,
    incidentLocationSource: null,
    incidentLocationMunicipality: null,
    incidentLocationProvince: null,
    incidentLocationCountry: null,
  }
}

/** Form fields to wipe when unchecking "Requires field engineer dispatch". */
export function clearedDispatchContextFields(): Pick<
  ParentTicketFormValues,
  | 'storeId'
  | 'storeCode'
  | 'incidentLocationType'
  | 'incidentLocationName'
  | 'incidentLocationAddress'
  | 'incidentLocationLatitude'
  | 'incidentLocationLongitude'
  | 'incidentLocationPlaceId'
  | 'incidentLocationSource'
  | 'incidentLocationMunicipality'
  | 'incidentLocationProvince'
  | 'incidentLocationCountry'
  | 'primaryEngineerId'
  | 'additionalFeUserIds'
  | 'scheduledAt'
> {
  return {
    storeId: '',
    storeCode: '',
    ...clearedIncidentLocationFields(),
    primaryEngineerId: null,
    additionalFeUserIds: [],
    scheduledAt: undefined,
  }
}

/** Build API request body for create/update parent ticket. */
export function buildParentTicketApiBody(
  payload: ParentTicketFormValues,
  options?: { includeAssets?: boolean; requiresDispatch?: boolean; clearDispatchContext?: boolean },
): Record<string, unknown> {
  const body: Record<string, unknown> = {}

  const set = (key: string, value: unknown) => {
    if (value != null && value !== '') body[key] = value
  }
  const setRequired = (key: string, value: unknown) => {
    body[key] = value != null ? String(value) : ''
  }

  set('requestId', payload.requestId)
  if (payload.clientId != null) set('clientId', payload.clientId)
  if (payload.storeId === '') {
    body.storeId = ''
    body.storeCode = ''
  } else if (payload.storeId != null) {
    set('storeId', payload.storeId)
    set('storeCode', payload.storeCode ?? '')
  } else {
    set('storeCode', payload.storeCode ?? '')
  }
  if (options?.clearDispatchContext) {
    body.storeId = ''
    body.storeCode = ''
    body.incidentLocationType = null
    body.incidentLocationName = null
    body.incidentLocationAddress = null
    body.incidentLocationLatitude = null
    body.incidentLocationLongitude = null
    body.incidentLocationPlaceId = null
    body.incidentLocationSource = null
    body.incidentLocationMunicipality = null
    body.incidentLocationProvince = null
    body.incidentLocationCountry = null
  } else if (options?.requiresDispatch !== false) {
    set('incidentLocationType', payload.incidentLocationType)
    set('incidentLocationName', payload.incidentLocationName)
    set('incidentLocationAddress', payload.incidentLocationAddress)
    if (payload.incidentLocationLatitude != null) set('incidentLocationLatitude', payload.incidentLocationLatitude)
    if (payload.incidentLocationLongitude != null) set('incidentLocationLongitude', payload.incidentLocationLongitude)
    set('incidentLocationPlaceId', payload.incidentLocationPlaceId)
    set('incidentLocationSource', payload.incidentLocationSource)
    set('incidentLocationMunicipality', payload.incidentLocationMunicipality)
    set('incidentLocationProvince', payload.incidentLocationProvince)
    set('incidentLocationCountry', payload.incidentLocationCountry)
  }
  setRequired('subject', payload.subject)
  setRequired('severity', payload.severity ?? payload.priority ?? '')
  if (payload.supportType !== undefined) set('supportType', payload.supportType)
  setRequired('source', payload.source)
  setRequired('issueDescription', payload.issueDescription)
  set('agentId', payload.agentId ?? '')
  if (payload.serviceTypeId != null) set('serviceTypeId', payload.serviceTypeId)
  if (payload.clientReferenceNumber) set('clientReferenceNumber', payload.clientReferenceNumber)
  if (payload.billable !== undefined) body.billable = payload.billable
  if (payload.assignedTeamId != null) set('assignedTeamId', payload.assignedTeamId)
  if (payload.resolverTeamId != null) set('resolverTeamId', payload.resolverTeamId)
  // Explicit clears must reach the server when an ancestor changes.
  for (const field of ['issueCategoryId', 'issueSubCategoryId', 'issueItemId'] as const) {
    if (Object.prototype.hasOwnProperty.call(payload, field)) body[field] = payload[field] ?? null
  }
  if (payload.regionId != null) set('regionId', payload.regionId)
  if (payload.areaClusterId != null) set('areaClusterId', payload.areaClusterId)
  if (payload.provinceId != null) set('provinceId', payload.provinceId)
  if (payload.townId != null) set('townId', payload.townId)
  if (payload.clientPurchaseOrderId !== undefined) body.clientPurchaseOrderId = payload.clientPurchaseOrderId
  if (payload.notes) set('notes', payload.notes)
  if (payload.submitTicket) set('submitTicket', '1')
  if (payload.dispatchPlan) {
    body.dispatchPlan = payload.dispatchPlan
    body.expectedDispatchVersion = payload.expectedDispatchVersion
    body.dispatchChangeReason = payload.dispatchChangeReason
  }
  if (payload.hasPartsNeeded !== undefined) set('hasPartsNeeded', payload.hasPartsNeeded)
  if (payload.confirmRemoveLinkedParts) set('confirmRemoveLinkedParts', true)
  if (payload.parts !== undefined) {
    const rows = Array.isArray(payload.parts)
      ? payload.parts.filter((row) => (row.part_name ?? '').trim() !== '')
      : []
    set('parts', rows.length > 0 ? JSON.stringify(rows) : '[]')
  }
  if (options?.includeAssets !== false && payload.assets !== undefined) {
    set('assets', Array.isArray(payload.assets) && payload.assets.length > 0 ? JSON.stringify(payload.assets) : '[]')
  }

  const certificationRequired = Boolean(payload.certificationRequired)
  body.certificationRequired = certificationRequired
  body.brandId = certificationRequired && payload.brandId != null ? Number(payload.brandId) : null

  if (options?.clearDispatchContext) {
    body.storeId = ''
    body.storeCode = ''
    body.incidentLocationType = null
    body.incidentLocationName = null
    body.incidentLocationAddress = null
    body.incidentLocationLatitude = null
    body.incidentLocationLongitude = null
    body.incidentLocationPlaceId = null
    body.incidentLocationSource = null
    body.incidentLocationMunicipality = null
    body.incidentLocationProvince = null
    body.incidentLocationCountry = null
    body.primaryEngineerId = null
    body.additionalFeUserIds = []
    body.scheduledAt = null
    body.certificationRequired = false
    body.brandId = null
  } else {
    if ('primaryEngineerId' in payload) {
      body.primaryEngineerId =
        payload.primaryEngineerId === '' || payload.primaryEngineerId == null
          ? null
          : Number(payload.primaryEngineerId)
    }
    if ('additionalFeUserIds' in payload || 'backupEngineerIds' in payload) {
      const ids = payload.additionalFeUserIds ?? payload.backupEngineerIds
      body.additionalFeUserIds = Array.isArray(ids)
        ? ids.map(Number).filter((id) => id > 0)
        : []
    }
  }

  return body
}

function mapPartsFromTicket(ext: Record<string, unknown>): TicketPartFormRow[] {
  const raw = ext.ticket_parts ?? ext.ticketParts
  if (!Array.isArray(raw)) return []

  return raw
    .map((row) => {
      const item = row as Record<string, unknown>
      const taskIds = Array.isArray(item.task_ids)
        ? (item.task_ids as unknown[]).map((id) => Number(id)).filter((id) => Number.isFinite(id))
        : Array.isArray(item.taskIds)
          ? (item.taskIds as unknown[]).map((id) => Number(id)).filter((id) => Number.isFinite(id))
          : item.child_ticket_id != null || item.childTicketId != null
            ? [Number(item.child_ticket_id ?? item.childTicketId)]
            : []
      const primaryTask = Array.isArray(item.tasks) && item.tasks.length > 0
        ? (item.tasks[0] as Record<string, unknown>)
        : null

      return {
        id: toNumberOrUndefined(item.id),
        part_name: String(item.part_name ?? item.partName ?? ''),
        quantity:
          item.quantity != null && item.quantity !== ''
            ? String(item.quantity)
            : '',
        unit: item.unit != null ? String(item.unit) : '',
        notes: item.notes != null ? String(item.notes) : '',
        addedAsTask: Boolean(item.added_as_task ?? item.addedAsTask ?? taskIds.length > 0),
        childTicketId: taskIds[0] ?? toNumberOrUndefined(item.child_ticket_id ?? item.childTicketId),
        taskIds,
        taskNumber: primaryTask?.task_number != null ? String(primaryTask.task_number) : null,
      }
    })
    .filter((row) => row.part_name.trim() !== '' || row.id != null)
}

/** Merge dispatch selection store context into form values before save. */
export function buildEffectiveFormForSubmit(
  form: ParentTicketFormValues,
  options?: {
    requiresDispatch?: boolean
    dispatchSelection?: FEDispatchSelection | null
  },
): ParentTicketFormValues {
  if (!options?.requiresDispatch) {
    return {
      ...form,
      ...clearedIncidentLocationFields(),
    }
  }

  if (!options.dispatchSelection) {
    return form
  }

  if (options.dispatchSelection.locationType === 'location') {
    const name = options.dispatchSelection.locationDisplayName ?? options.dispatchSelection.storeName
    const source =
      options.dispatchSelection.locationSource ??
      form.incidentLocationSource ??
      'nominatim'
    return {
      ...form,
      storeId: '',
      storeCode: '',
      incidentLocationType: 'location',
      incidentLocationName: name,
      incidentLocationAddress: options.dispatchSelection.storeAddress || name,
      incidentLocationLatitude: options.dispatchSelection.latitude,
      incidentLocationLongitude: options.dispatchSelection.longitude,
      incidentLocationPlaceId: form.incidentLocationPlaceId ?? null,
      incidentLocationSource: source,
      incidentLocationMunicipality: options.dispatchSelection.incidentLocationMunicipality ?? form.incidentLocationMunicipality ?? null,
      incidentLocationProvince: options.dispatchSelection.incidentLocationProvince ?? form.incidentLocationProvince ?? null,
      incidentLocationCountry: options.dispatchSelection.incidentLocationCountry ?? form.incidentLocationCountry ?? null,
    }
  }

  const { storeId, storeCode } = options.dispatchSelection
  const storeSource =
    options.dispatchSelection.locationSource === 'manual_pin' ||
    options.dispatchSelection.locationSource === 'store_geocoded_address'
      ? options.dispatchSelection.locationSource
      : 'store'
  return {
    ...form,
    storeId: storeId ?? undefined,
    storeCode: storeCode ?? form.storeCode ?? '',
    incidentLocationType: 'store' as IncidentLocationType,
    incidentLocationName: options.dispatchSelection.storeName,
    incidentLocationAddress: options.dispatchSelection.storeAddress,
    incidentLocationLatitude: options.dispatchSelection.latitude,
    incidentLocationLongitude: options.dispatchSelection.longitude,
    incidentLocationSource: storeSource,
    incidentLocationMunicipality:
      storeSource === 'manual_pin'
        ? options.dispatchSelection.incidentLocationMunicipality ?? form.incidentLocationMunicipality ?? null
        : null,
    incidentLocationProvince:
      storeSource === 'manual_pin'
        ? options.dispatchSelection.incidentLocationProvince ?? form.incidentLocationProvince ?? null
        : null,
    incidentLocationCountry:
      storeSource === 'manual_pin'
        ? options.dispatchSelection.incidentLocationCountry ?? form.incidentLocationCountry ?? null
        : null,
  }
}

/** Metadata subset used by ParentTicketForm save handlers. */
export function buildMetadataPayload(
  form: ParentTicketFormValues,
  submitTicket: boolean,
  options?: { requiresDispatch?: boolean },
): ParentTicketFormValues {
  return {
    ...form,
    submitTicket,
    requiresDispatch: options?.requiresDispatch,
  }
}
